import React, { useState, useEffect, useCallback } from 'react';
import { X, Clock, User, Mail, Edit, FileText, Tag, DollarSign, Package, MessageSquare, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService } from '../../services/apiService';
import LoadingSpinner from './LoadingSpinner';
import toast from 'react-hot-toast';
import { formatDateTime } from '../../utils/formatDateTime';
import { useSettings } from '../../contexts/SettingsContext';

interface ActivityLog {
  _id: string;
  action: string;
  performedBy: {
    _id: string;
    name: string;
    email: string;
  };
  performedAt: string;
  details: {
    field?: string;
    oldValue?: any;
    newValue?: any;
    description?: string;
  };
  metadata: {
    emailRecipients?: string[];
    emailSubject?: string;
    statusFrom?: string;
    statusTo?: string;
    itemName?: string;
    itemQuantity?: number;
    paymentAmount?: number;
    paymentMethod?: string;
    noteContent?: string;
  };
}

interface ActivityHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'purchaseInvoice' | 'deliveryInvoice' | 'returnInvoice';
  entityId: string;
  entityName: string;
}

const ActivityHistoryModal: React.FC<ActivityHistoryModalProps> = ({
  isOpen,
  onClose,
  entityType,
  entityId,
  entityName
}) => {
  const { settings } = useSettings();
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchLogs = useCallback(async (pageNum = 1, append = false) => {
    try {
      setLoading(pageNum === 1);
      setRefreshing(pageNum > 1);

      const response = await apiService.get(`/activity-logs/${entityType}/${entityId}/paginated?page=${pageNum}&limit=20`);
      
      if (response.data.success) {
        const newLogs = response.data.data;
        const pagination = response.data.pagination;

        if (append) {
          setLogs(prev => [...prev, ...newLogs]);
        } else {
          setLogs(newLogs);
        }

        setHasMore(pageNum < pagination.pages);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Failed to fetch activity history');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (isOpen && entityId) {
      fetchLogs(1, false);
    }
  }, [isOpen, entityId, fetchLogs]);

  const handleRefresh = () => {
    fetchLogs(1, false);
  };

  const handleLoadMore = () => {
    if (hasMore && !refreshing) {
      fetchLogs(page + 1, true);
    }
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'created':
        return <FileText className="w-4 h-4 text-green-600" />;
      case 'updated':
        return <Edit className="w-4 h-4 text-blue-600" />;
      case 'status_changed':
        return <Tag className="w-4 h-4 text-purple-600" />;
      case 'emailed':
        return <Mail className="w-4 h-4 text-orange-600" />;
      case 'item_added':
      case 'item_removed':
      case 'item_updated':
        return <Package className="w-4 h-4 text-indigo-600" />;
      case 'payment_received':
      case 'payment_updated':
        return <DollarSign className="w-4 h-4 text-green-600" />;
      case 'note_added':
      case 'note_updated':
      case 'note_deleted':
        return <MessageSquare className="w-4 h-4 text-yellow-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'created':
        return 'Created';
      case 'updated':
        return 'Updated';
      case 'status_changed':
        return 'Status Changed';
      case 'emailed':
        return 'Emailed';
      case 'item_added':
        return 'Item Added';
      case 'item_removed':
        return 'Item Removed';
      case 'item_updated':
        return 'Item Updated';
      case 'payment_received':
        return 'Payment Received';
      case 'payment_updated':
        return 'Payment Updated';
      case 'note_added':
        return 'Note Added';
      case 'note_updated':
        return 'Note Updated';
      case 'note_deleted':
        return 'Note Deleted';
      default:
        return action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'created':
        return 'bg-green-100 text-green-800';
      case 'updated':
        return 'bg-blue-100 text-blue-800';
      case 'status_changed':
        return 'bg-purple-100 text-purple-800';
      case 'emailed':
        return 'bg-orange-100 text-orange-800';
      case 'item_added':
      case 'item_removed':
      case 'item_updated':
        return 'bg-indigo-100 text-indigo-800';
      case 'payment_received':
      case 'payment_updated':
        return 'bg-green-100 text-green-800';
      case 'note_added':
      case 'note_updated':
      case 'note_deleted':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[var(--color-surface)] text-[var(--color-text)] border border-[var(--color-border)] rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[var(--color-border)] bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
            <div className="flex items-center space-x-3">
              <Clock className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-semibold">Activity History</h2>
                <p className="text-blue-100 text-sm">{entityName}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="p-2 text-white hover:text-gray-200 transition-colors rounded-full hover:bg-white hover:bg-opacity-20 bg-transparent"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-white hover:text-gray-200 transition-colors rounded-full hover:bg-white hover:bg-opacity-20"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <LoadingSpinner size="lg" />
              </div>
            ) : logs.length === 0 ? (
              <div className="flex items-center justify-center h-64 text-[var(--color-text-secondary)]">
                <div className="text-center">
                  <Clock className="w-12 h-12 mx-auto mb-4 text-[var(--color-text-secondary)] opacity-40" />
                  <p className="text-lg font-medium text-[var(--color-text)]">No activity found</p>
                  <p className="text-sm">This invoice has no recorded activity yet.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {logs.map((log, index) => (
                  <motion.div
                    key={log._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-[var(--color-background)] rounded-lg p-4 border border-[var(--color-border)] text-[var(--color-text)]"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 mt-1">
                        {getActionIcon(log.action)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                              {getActionLabel(log.action)}
                            </span>
                             <span className="text-sm text-[var(--color-text-secondary)]">
                               {formatDateTime(log.performedAt, settings)}
                             </span>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2 mb-2">
                          <User className="w-4 h-4 text-[var(--color-text-secondary)]" />
                          <span className="text-sm font-medium text-[var(--color-text)]">
                            {log.performedBy?.name || 'Unknown User'}
                          </span>
                          <span className="text-sm text-[var(--color-text-secondary)]">
                            ({log.performedBy?.email || 'No email'})
                          </span>
                        </div>

                        {log.details?.description && (
                          <p className="text-sm text-[var(--color-text)] mb-2">
                            {log.details.description}
                          </p>
                        )}

                        {/* Additional metadata display */}
                        {log.metadata && (
                          <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                            {log.metadata.emailRecipients && (
                              <div className="text-sm text-[var(--color-text-secondary)] mb-1">
                                <span className="font-medium text-[var(--color-text)]">Recipients:</span> {log.metadata.emailRecipients.join(', ')}
                              </div>
                            )}
                            {log.metadata.emailSubject && (
                              <div className="text-sm text-[var(--color-text-secondary)] mb-1">
                                <span className="font-medium text-[var(--color-text)]">Subject:</span> {log.metadata.emailSubject}
                              </div>
                            )}
                            {log.metadata.statusFrom && log.metadata.statusTo && (
                              <div className="text-sm text-[var(--color-text-secondary)] mb-1">
                                <span className="font-medium text-[var(--color-text)]">Status:</span> {log.metadata.statusFrom} → {log.metadata.statusTo}
                              </div>
                            )}
                            {log.metadata.itemName && (
                              <div className="text-sm text-[var(--color-text-secondary)] mb-1">
                                <span className="font-medium text-[var(--color-text)]">Item:</span> {log.metadata.itemName}
                                {log.metadata.itemQuantity && ` (Qty: ${log.metadata.itemQuantity})`}
                              </div>
                            )}
                            {log.metadata.paymentAmount && (
                              <div className="text-sm text-[var(--color-text-secondary)] mb-1">
                                <span className="font-medium text-[var(--color-text)]">Payment:</span> ${log.metadata.paymentAmount}
                                {log.metadata.paymentMethod && ` via ${log.metadata.paymentMethod}`}
                              </div>
                            )}
                            {log.metadata.noteContent && (
                              <div className="text-sm text-[var(--color-text-secondary)] mb-1">
                                <span className="font-medium text-[var(--color-text)]">Note:</span> {log.metadata.noteContent}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}

                {/* Load More Button */}
                {hasMore && (
                  <div className="text-center pt-4">
                    <button
                      onClick={handleLoadMore}
                      disabled={refreshing}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {refreshing ? 'Loading...' : 'Load More'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ActivityHistoryModal;
